// 보행 그래프 관리 데이터 레이어. 다른 cmsContent 문서와 달리 그래프는 크기 제한 때문에
// 여러 청크 문서로 나뉘어 있다(campusGraphContent/versions/items/{version}/chunks/{index}).
// "부분 저장된 그래프가 production에 노출되면 안 된다" 요구 때문에 배포 순서가 중요하다:
//   1) 새 버전의 청크를 쓴다 — 이 시점엔 어떤 문서도 이 버전을 가리키지 않아 노출 위험이 없다.
//   2) 방금 쓴 청크를 다시 읽어 체크섬을 재검증한다.
//   3) 버전 매니페스트를 쓴다(여전히 production은 안 바뀜).
//   4) production 포인터를 새 버전으로 바꾸는 단일 문서 쓰기만 원자적으로 실행한다 — 이 한
//      줄이 실행되기 전까지 앱은 새 그래프를 절대 볼 수 없다.
// 롤백은 새 버전을 만들지 않고 production 포인터만 과거 버전으로 되돌린다(이미 존재하는
// 불변 버전 데이터를 재사용 — 청크를 다시 쓰지 않으므로 항상 즉시·안전하다).
import { collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore'
import { addAudit, formatDateTime, requireDb, toDate } from './data'
import {
  chunkCampusGraphItems, computeCampusGraphChecksum, mergeCampusGraphChunks, validateCampusGraphDraft,
} from '../../../../packages/validation/campusGraph'
import { CAMPUS_GRAPH_SCHEMA_VERSION } from '../../../../packages/types/campusGraph'

const BATCH_SIZE = 450 // Firestore 500 op 제한보다 여유 있게

function productionRef(db) { return doc(db, 'campusGraphContent', 'production') }
function versionManifestRef(db, version) { return doc(db, 'campusGraphContent', 'versions', 'items', String(version)) }
function versionChunksCollection(db, version) { return collection(db, 'campusGraphContent', 'versions', 'items', String(version), 'chunks') }
function draftManifestRef(db) { return doc(db, 'campusGraphContent', 'draft') }
function draftChunksCollection(db) { return collection(db, 'campusGraphContent', 'draftChunks', 'items') }

async function commitOpsInBatches(db, ops) {
  for (let i = 0; i < ops.length; i += BATCH_SIZE) {
    const batch = writeBatch(db)
    for (const op of ops.slice(i, i + BATCH_SIZE)) {
      if (op.type === 'delete') batch.delete(op.ref)
      else batch.set(op.ref, op.data)
    }
    await batch.commit()
  }
}

async function fetchChunkedGraph(chunksCollectionRef) {
  const snapshot = await getDocs(query(chunksCollectionRef, orderBy('index', 'asc')))
  return mergeCampusGraphChunks(snapshot.docs.map(item => item.data()))
}

export async function fetchCampusGraphContent() {
  const db = requireDb()
  const [draftManifestSnap, pointerSnap] = await Promise.all([getDoc(draftManifestRef(db)), getDoc(productionRef(db))])
  const draftManifest = draftManifestSnap.exists() ? draftManifestSnap.data() : null
  const pointer = pointerSnap.exists() ? pointerSnap.data() : null

  const [draftData, productionResult] = await Promise.all([
    draftManifest ? fetchChunkedGraph(draftChunksCollection(db)) : Promise.resolve(null),
    pointer ? getDoc(versionManifestRef(db, pointer.version)) : Promise.resolve(null),
  ])

  let production = null
  let productionManifest = null
  if (pointer && productionResult?.exists()) {
    productionManifest = productionResult.data()
    const data = await fetchChunkedGraph(versionChunksCollection(db, pointer.version))
    production = { ...data, buildingEntranceNodeMap: productionManifest.buildingEntranceNodeMap ?? {} }
  }

  const draft = draftManifest ? { ...draftData, buildingEntranceNodeMap: draftManifest.buildingEntranceNodeMap ?? {} } : null
  return { draft, draftManifest, production, productionManifest }
}

export async function fetchCampusGraphVersions(max = 20) {
  const db = requireDb()
  const snapshot = await getDocs(query(collection(db, 'campusGraphContent', 'versions', 'items'), orderBy('version', 'desc'), limit(max)))
  return snapshot.docs.map(item => item.data())
}

export async function saveCampusGraphDraft(data, { uid, baseVersion = 0 }) {
  const db = requireDb()
  const existingChunksSnap = await getDocs(draftChunksCollection(db))
  const chunks = chunkCampusGraphItems(data)
  const ops = [
    ...existingChunksSnap.docs.map(item => ({ type: 'delete', ref: item.ref })),
    ...chunks.map((items, index) => ({ type: 'set', ref: doc(draftChunksCollection(db), String(index)), data: { index, items } })),
  ]
  await commitOpsInBatches(db, ops)

  const manifest = {
    contentType: 'campusGraph', schemaVersion: CAMPUS_GRAPH_SCHEMA_VERSION, chunkCount: chunks.length,
    nodeCount: data.nodes?.length ?? 0, edgeCount: data.edges?.length ?? 0,
    buildingEntranceNodeMap: data.buildingEntranceNodeMap ?? {}, baseVersion, savedAt: serverTimestamp(), savedBy: uid,
  }
  const batch = writeBatch(db)
  batch.set(draftManifestRef(db), manifest)
  addAudit(batch, { action: 'draft-save', entityType: 'cms-campus-graph', entityId: 'campusGraph', actorUid: uid, before: null, after: { contentType: 'campusGraph', itemCount: manifest.nodeCount + manifest.edgeCount } })
  await batch.commit()
}

export async function discardCampusGraphDraft(uid) {
  const db = requireDb()
  const chunksSnap = await getDocs(draftChunksCollection(db))
  await commitOpsInBatches(db, chunksSnap.docs.map(item => ({ type: 'delete', ref: item.ref })))
  const batch = writeBatch(db)
  batch.delete(draftManifestRef(db))
  addAudit(batch, { action: 'delete', entityType: 'cms-campus-graph', entityId: 'campusGraph', actorUid: uid, before: null, after: null })
  await batch.commit()
}

export async function publishCampusGraph(data, { uid }) {
  const validation = validateCampusGraphDraft(data)
  if (!validation.valid) {
    const error = new Error('치명적 오류가 있어 배포할 수 없습니다.')
    error.validation = validation
    throw error
  }

  const db = requireDb()
  const pointerSnap = await getDoc(productionRef(db))
  const nextVersion = (pointerSnap.exists() ? pointerSnap.data().version : 0) + 1
  const checksum = await computeCampusGraphChecksum(data)
  const chunks = chunkCampusGraphItems(data)

  // 1) 청크 저장 — 아직 어떤 포인터도 이 버전을 가리키지 않는다.
  await commitOpsInBatches(db, chunks.map((items, index) => ({ type: 'set', ref: doc(versionChunksCollection(db, nextVersion), String(index)), data: { index, items } })))

  // 2) 재검증 — 방금 쓴 것을 다시 읽어 체크섬이 실제로 일치하는지 확인한다.
  const merged = await fetchChunkedGraph(versionChunksCollection(db, nextVersion))
  if (merged.nodes.length !== data.nodes.length || merged.edges.length !== data.edges.length) {
    throw new Error('청크 저장 검증에 실패했습니다(개수 불일치). 다시 시도해 주세요.')
  }
  const verifyChecksum = await computeCampusGraphChecksum(merged)
  if (verifyChecksum !== checksum) throw new Error('청크 저장 검증에 실패했습니다(체크섬 불일치). 다시 시도해 주세요.')

  // 3) 버전 매니페스트 저장 — production은 여전히 이전 버전을 가리킨다.
  const manifest = {
    contentType: 'campusGraph', schemaVersion: CAMPUS_GRAPH_SCHEMA_VERSION, version: nextVersion,
    chunkCount: chunks.length, checksum, nodeCount: data.nodes.length, edgeCount: data.edges.length,
    buildingEntranceNodeMap: data.buildingEntranceNodeMap ?? {}, publishedAt: serverTimestamp(), publishedBy: uid, note: null,
  }
  const manifestBatch = writeBatch(db)
  manifestBatch.set(versionManifestRef(db, nextVersion), manifest)
  await manifestBatch.commit()

  // 4) 마지막으로 production 포인터만 원자적으로 전환한다 — 이 단일 문서 쓰기 전까지 앱은 새
  //    그래프를 절대 보지 못한다.
  const draftChunksSnap = await getDocs(draftChunksCollection(db))
  const pointerBatch = writeBatch(db)
  pointerBatch.set(productionRef(db), { contentType: 'campusGraph', version: nextVersion })
  pointerBatch.delete(draftManifestRef(db))
  addAudit(pointerBatch, { action: 'publish', entityType: 'cms-campus-graph', entityId: 'campusGraph', actorUid: uid, before: null, after: { contentType: 'campusGraph', version: nextVersion } })
  await pointerBatch.commit()
  await commitOpsInBatches(db, draftChunksSnap.docs.map(item => ({ type: 'delete', ref: item.ref })))

  return nextVersion
}

export async function rollbackCampusGraph(targetVersion, { uid }) {
  const db = requireDb()
  const manifestSnap = await getDoc(versionManifestRef(db, targetVersion))
  if (!manifestSnap.exists()) throw new Error(`버전 ${targetVersion}을(를) 찾을 수 없습니다.`)
  const pointerSnap = await getDoc(productionRef(db))
  const before = pointerSnap.exists() ? pointerSnap.data().version : null

  const batch = writeBatch(db)
  batch.set(productionRef(db), { contentType: 'campusGraph', version: targetVersion })
  addAudit(batch, { action: 'rollback', entityType: 'cms-campus-graph', entityId: 'campusGraph', actorUid: uid, before: { version: before }, after: { contentType: 'campusGraph', version: targetVersion } })
  await batch.commit()
  return targetVersion
}

export { formatDateTime, toDate, validateCampusGraphDraft }
